import { useReducer } from 'react';

export function useFormControls(controlsArray, initialStateOverride = {}) {
  /* initialState is derived from the controlsArray
  controlsArray is an array of objects with the shape:
    {fieldName: string; !! Must match the 'name' property of the form field !!
    value: string | number | boolean,
    validators: []
    sanitizers: []}
  initialStateOverride is an optional object that contains the key-value pairs
    to initialize the state of the fields.
    */
  const initialState = controlsArray.reduce(
    (stateObject, control) => {
      const controlValue = initialStateOverride[control.fieldName] ?? control.value;
      const fields = { // stateObject is the accumulator object
        ...stateObject,
        [control.fieldName]: {
          name: control.fieldName,
          clean_value: controlValue, // starting value from external data (db, etc.)
          value: controlValue, // current value in the form field
          touched: false, // true if user has interacted with the field
          dirty: false, // true if value has changed from clean_value
          valid: !control?.validators?.length, // true if no validators or all validators pass
          errors: control?.validators?.length // array of error messages from validators
            ? control.validators.map((validator) => validator(controlValue))
            : [],
          validators: control.validators ?? [], // array of functions that validate the field
          sanitizers: control.sanitizers ?? [], // array of functions that sanitize the field
          placeholder: control.placeholder ?? '', // text to display in the field when empty
          label: control.label ?? '', // text to display as the field label
          description: control.description ?? '', // field description / helper text
        },
      };
      return fields;
    },
    {}, // stateObject
  );
  // dispatch for this reducer is private to the hook and should be wrapped by functions below.
  const [fields, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'INITIALIZE':
          return action.payload;
        case 'SET_FIELD':
          return {
            ...state,
            [action.payload.fieldName]: {
              ...state[action.payload.fieldName],
              ...action.payload.value,
            },
          };
        case 'SET_FORM':
          return {
            ...action.payload,
          };
        case 'SET_VALIDATORS':
          return {
            ...state,
            [action.payload.fieldName]: {
              ...state[action.payload.fieldName],
              validators: action.payload.validators,
            },
          };
        case 'RESET_FIELD':
          return {
            ...state,
            [action.payload.fieldName]: {
              ...state[action.payload.fieldName],
              value: initialState[action.payload.fieldName].value,
              touched: initialState[action.payload.fieldName].touched,
              valid: initialState[action.payload.fieldName].valid,
              errors: initialState[action.payload.fieldName].errors,
              validators: initialState[action.payload.fieldName].validators,
            },
          };
        default:
          return state;
      }
    }, initialState,
  );

  const getFormValues = () => {
    const formValues = {};
    Object.keys(fields).forEach((fieldName) => {
      formValues[fieldName] = fields[fieldName].value;
    });
    return formValues;
  };

  // validateField is an event handler that validates and sets the value of a field.
  // Provide it to onChange or onBlur (or any other event) to validate during that event.
  const validateField = (event) => {
    // eslint-disable-next-line object-curly-newline
    const { name: fieldName, value, type, checked } = event.target; // destructure the event.
    if (!fields[fieldName]) { // Confirm that the field exists and throw an error if not.
      throw new Error(`Error in setField: '${fieldName}' is not a valid field`);
    }
    let realValue = value;
    if (type === 'checkbox') {
      // if the field is a checkbox, set the value to checked
      realValue = checked;
    } else {
      // otherwise, set the value to the value of the event
      realValue = fields[fieldName].sanitizers.reduce(
        (sanitized, sanitizer) => sanitizer(sanitized) ?? sanitized, // sanitized == accumulator
        value, // starting value
      );
    }

    const { validators } = fields[fieldName]; // Get the validators array.
    const errors = validators.reduce( // Iterate over the validators array.
      (existingErrors, validator) => { // existingErrors is the accumulator array.
        const error = validator(realValue); // Run validators.
        if (error) { // If an error is returned, add it to the accumulator array.
          return [...existingErrors, error];
        }
        return existingErrors;
      }, [],
    );
    dispatch({ // call dispatch to update the form in state
      type: 'SET_FIELD',
      payload: {
        fieldName,
        value: {
          value: realValue,
          touched: true,
          dirty: !fields[fieldName].clean_value
            || (fields[fieldName].clean_value && realValue !== fields[fieldName].clean_value),
          valid: errors.length === 0,
          errors,
        },
      },
    });
  };
  // setField is an onChange event handler that updates the value of the field without validating.
  const setField = (event) => {
    // eslint-disable-next-line object-curly-newline
    const { name: fieldName, value, type, checked } = event.target; // destructure the event.
    if (!fields[fieldName]) { // confirm that the field exists and throw an error if not.
      throw new Error(`Error in setField: '${fieldName}' is not a valid field`);
    }
    const realValue = type === 'checkbox' ? checked : value;
    dispatch({
      type: 'SET_FIELD', // update the value of the form field to value, or checked if checkbox.
      payload: {
        fieldName,
        value: {
          value: realValue,
          touched: true,
          dirty: !fields[fieldName].clean_value
            || (fields[fieldName].clean_value && realValue !== fields[fieldName].clean_value),
          errors: [],
        },
      },
    });
  };

  // setFieldWithError is an onChange event handler that updates the value of
  // the field without validating and sets a feild's error message.
  const setFieldWithError = (event, error = null) => {
    // eslint-disable-next-line object-curly-newline
    const { name: fieldName, value, type, checked } = event.target; // destructure the event.
    if (!fields[fieldName]) { // confirm that the field exists and throw an error if not.
      throw new Error(`Error in setFieldWithError: '${fieldName}' is not a valid field`);
    }
    const realValue = type === 'checkbox' ? checked : value;
    const validValue = error === null;
    const errorsValue = validValue ? [] : [error];

    dispatch({
      // call dispatch to update the form in state
      type: 'SET_FIELD',
      payload: {
        fieldName,
        value: {
          value: realValue,
          touched: true,
          dirty: !fields[fieldName].clean_value
            || (fields[fieldName].clean_value && realValue !== fields[fieldName].clean_value),
          valid: validValue,
          errors: errorsValue,
        },
      },
    });
  };

  const setFieldValue = (fieldName, value) => {
    setField({
      target: {
        name: fieldName,
        value,
      },
    });
  };

  // setValidators allows us to change validations dynamically
  // (e.g., selecting one option sets another field to required).
  const setValidators = (fieldName, validators) => {
    if (!fields[fieldName]) { // confirm that the field exists and throw an error if not.
      throw new Error(`Error in setValidators: '${fieldName}' is not a valid field`);
    }
    dispatch({
      type: 'SET_VALIDATORS',
      payload: {
        fieldName,
        validators,
      },
    });
  };

  const setError = (fieldName, error = null) => {
    if (!fields[fieldName]) {
      // confirm that the field exists and throw an error if not.
      throw new Error(`Error in setError: '${fieldName}' is not a valid field`);
    }
    const validValue = error === null;
    const errorsValue = validValue ? [] : [error];
    dispatch({
      // call dispatch to update the form in state
      type: 'SET_FIELD',
      payload: {
        fieldName,
        value: {
          value: fields[fieldName].value,
          touched: true,
          valid: validValue,
          errors: errorsValue,
        },
      },
    });
  };

  const setErrors = (formErrors, callback = (e) => e.message) => {
    const newState = { ...fields };
    Object.keys(formErrors).forEach((field) => {
      const fieldErrors = formErrors[field];
      const messages = fieldErrors.map(callback);
      newState[field] = {
        ...fields[field],
        errors: messages,
        valid: false,
        touched: true,
      };
    });
    dispatch({
      type: 'SET_FORM',
      payload: newState,
    });
  };

  // resetField wraps dispatch to reset a field to its initial state
  const resetField = (fieldName) => {
    dispatch({
      type: 'RESET_FIELD',
      payload: { fieldName },
    });
  };
  // resetForm wraps dispatch to reset the whole form to its initial state
  const resetForm = () => {
    dispatch({
      type: 'INITIALIZE',
      payload: initialState,
    });
  };

  const updateForm = (data) => {
    // update the values of the form fields by finding the corresponding field in data
    // and setting the value to the value in data
    const newState = {};
    Object.keys(fields).forEach((fieldName) => {
      newState[fieldName] = {
        ...fields[fieldName],
        clean_value: data[fieldName] ?? '',
        value: data[fieldName] ?? fields[fieldName].value,
        dirty: false,
        errors: [], // todo: this might warrant some regression testing
        valid: !fields[fieldName].validators.length
          || fields[fieldName].validators.reduce(
            (valid, validator) => valid && !validator(data[fieldName]),
            true,
          ),
      };
    });
    dispatch({
      type: 'SET_FORM',
      payload: newState,
    });
  };

  // getErroringFieldNames updates validity in state & returns array of field names with errors.
  const getErroringFieldNames = () => {
    const erroringFieldNames = [];
    Object.keys(fields).forEach((fieldName) => { // iterate over all the keys in state
      const { validators } = fields[fieldName]; // get the validators array
      let { errors } = fields[fieldName];
      // TODO: change this back to setting errors to validators.reduce, and stop including previous
      // errors. This change was made to accomodate errors being set on fields that did NOT come
      // from a validator. Moving forward we want all field errors to come from validators.
      errors = errors.concat(validators.reduce( // iterate over the validators array
        (existingErrors, validator) => { // existingErrors is the accumulator array
          const error = validator(fields[fieldName].value); // run validators
          if (error) { // if an error is returned, add it to the accumulator array
            return [...existingErrors, error];
          }
          return existingErrors;
        }, [],
      ));
      // todo: related to above concat and can also be removed when that is fixed
      errors = [...new Set(errors)]; // select distinct
      dispatch({
        type: 'SET_FIELD',
        payload: {
          fieldName,
          value: {
            ...fields[fieldName],
            touched: true,
            valid: errors.length === 0,
            errors,
          },
        },
      });
      if (errors.length) erroringFieldNames.push(fieldName);
    });
    return erroringFieldNames;
  };

  const validateForm = () => {
    return getErroringFieldNames().length === 0;
  };

  return {
    fields,
    getFormValues,
    setField,
    setFieldWithError,
    setFieldValue,
    setValidators,
    setError,
    setErrors,
    resetField,
    updateForm,
    validateField,
    validateForm,
    getErroringFieldNames,
    resetForm,
  };
}

// Validator is a HOC that takes any boolean-returning callback and an error object/string/number
// It returns an error of the specified type/shape if the callback returns false
export const Validator = (callback, error) => {
  return (value) => { return !callback(value) ? error : null; };
};
// isRequired is a really simple example of a potential Validator callback
export const isRequired = (value) => !!value;
// validateEmailFormat had been duplicated in a few places, so I gave it a new home here
export const validateEmailFormat = (em) => {
  const regexp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexp.test(em);
};
export const validateUrl = (url) => {
  if (url.trim().length === 0) {
    // don't validate empty url in case it's not required
    return true;
  }
  let urlToCheck = url;
  // if the URL doesn't begin with a protocol, add https://
  if (!url.match(/^[a-zA-Z]+:\/\//)) {
    urlToCheck = `https://${urlToCheck}`;
  }

  try {
    // eslint-disable-next-line no-new
    new URL(urlToCheck);
    return true;
  } catch (err) {
    return false;
  }
};
